import { TdsFooterLite, BasicLayout } from '@tencent/tds-ui';
import { Spin } from 'antd';
import classnames from 'classnames';
import { isEmpty } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import React, { Fragment, useEffect } from 'react';
import {
  Link,
  useHistory, useLocation,
} from 'react-router-dom';
import BuglySgpIcon from '@/assets/images/public/bugly-sgp.react.svg?react';
import BuglyIcon from '@/assets/images/public/TDS-bugly-pre.react.svg?react';
import { MultiApp } from '@/config/context';
import { getUserId } from '@/infrastructure/auth';
import {
  getParamFromUrl,
  pushHistory,
} from '@/infrastructure/history';
import { storageGet } from '@/infrastructure/storage-utils';
import { ArrearsReminder } from '@/presentation/components/arrears-reminder';
import { Banner } from '@/presentation/components/banner-pro';
import useToggle from '@/presentation/components/toggle/use-toggle';
import { useAuthContext } from '@/presentation/hooks';
import { Content } from '@/presentation/layouts-pro/content';
import { TopMenu } from '@/presentation/layouts-pro/header';
import { AppList } from '@/presentation/layouts-pro/header/app-list';
import { SailingPlan } from '@/presentation/layouts-pro/header/sailing-plan';
import { Tools } from '@/presentation/layouts-pro/tools';
import {
  getEntriesRoutes,
  getOverlaysRoutes,
} from '@/presentation/routes/routes';
import { stores } from '@/presentation/store';
import { AppSlot } from '../components/app-slot';
import { SgpFooterLite } from '../components/sgp-footer';
import style from './index.module.scss';

export const ProAppLayout = observer(() => {
  const { productId, permission, setExceed, loaded, hasProduct, setLoaded, changeProduct } = stores.ProductStore;
  const urlProductId = getParamFromUrl('productId');
  const { collapsed, setCollapsed, layoutLoading, loadingMessage } = stores.LayoutStore;
  const { authInfo } = useAuthContext();

  const history = useHistory();
  const location = useLocation();
  const handlePath = (path: string) => {
    pushHistory(history, path);
  };
  const [enabled] = useToggle('trialApply', ['user']);
  const isConfig = location.pathname.includes('/config-manage');
  const isTrial = location.pathname.includes('/trial-apply');
  const isResourcePack = location.pathname.includes('/resource-pack');
  const liteModeRoutes = [
    '/workbench/apps',
    '/user/profile',
    '/user/coupons',
    '/config-manage',
    '/product-approval',
    '/product-approval/details',
    '/usage-details',
    '/resource-pack',
    '/order',
    '/resource-manage',
    '/expense',
    '/expense/order-details',
    '/consult',
    '/trial-apply',
    '/trial-approval',
    '/resource-manage-details',
    '/month-resource-details',
  ];

  // 窄版 最大1220
  const narrowRoutes = [
    '/resource-pack',
  ];

  const isNarrowMode = narrowRoutes.includes(location.pathname);

  const initRedirect = async () => {
    const oaName = getUserId();
    const storageProductId = storageGet(`${oaName}-productId`) ?? '';
    const storagePid = storageGet(`${oaName}-pid`) ?? '';
    const productId = urlProductId || storageProductId;
    const pid = getParamFromUrl('pid') || storagePid;
    const isValidPrams = !isEmpty(productId) && !isEmpty(pid);

    // 未登录，访问根路径，跳转首页
    if (location.pathname === '/' && !authInfo?.isLoggedIn) {
      history.push('/index/main');
      return;
    }

    // 根路径 ，如果有缓存productID，跳转概览页
    if (location.pathname === '/' && isValidPrams && hasProduct) {
      changeProduct(storageProductId, storagePid, '/overview');
      return;
    }

    // 无产品选中，跳首页 登录了情况才跳
    if (!urlProductId && !liteModeRoutes.includes(location.pathname)) {
      history.push('/index/main');
      return;
    }

    // 与产品无关的页面，直接render
    if (liteModeRoutes.includes(location.pathname)) {
      setLoaded(true);
    }
  };

  useEffect(() => {
    initRedirect();
  }, [location.pathname]);

  // productID变化重新执行初始化
  useEffect(() => {
    if (authInfo?.isLoggedIn) {
      stores.ProductStore.init();
    }
  }, [urlProductId]);

  useEffect(() => {
    // 等产品初始化完了，并且有权限情况下，再去拉流量包的信息, 不阻塞初始化
    if (loaded && productId && permission) {
      setExceed();
    }
  }, [loaded, productId, permission]);

  // 没登录不render
  if (!authInfo?.isLoggedIn) {
    return null;
  }

  return (
    <Spin
      spinning={layoutLoading}
      tip={loadingMessage}
      className={style.loading}
    >
      <BasicLayout
        mainMenuLink={'/overview'}
        narrow={isNarrowMode}
        liteMode={false}
        showBreadcrumb={hasProduct}
        className={classnames({
          [style.app]: true,
          [style.config]: isConfig,
          [style.trial]: isTrial,
          [style.resourcePack]: isResourcePack && enabled,
        })}
        leftMenuRoutes={{ routes: getEntriesRoutes() }}
        overlayMenuRoutes={{ routes: getOverlaysRoutes() }}
        appSelector={<AppList />}
        topMenu={<TopMenu />}
        sailingPlan={isResourcePack && enabled ? <SailingPlan /> : null}
        liteModeRoutes={liteModeRoutes}
        logo={
          <Link to={'/index/main'}>
            <AppSlot apps={[MultiApp.Pro]}><BuglyIcon /></AppSlot>
            <AppSlot apps={[MultiApp.Sgp]}><BuglySgpIcon /></AppSlot>
          </Link>
        }
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        handlePath={handlePath}
        title={'Bugly'}
        renderBanner={(showBanner, setShowBanner) => (
          <Banner showBanner={showBanner} setShowBanner={setShowBanner} />
        )}
        renderArrearsBanner={() => (!liteModeRoutes.includes(location.pathname) ? (
            <ArrearsReminder />
        ) : undefined)
        }
      >
        <div className={classnames(style.containerLayout, 'content-layout')}>
          <Content />
          {isTrial ? null : (
            <Fragment>
              <AppSlot apps={[MultiApp.Sgp]}>
                <SgpFooterLite
                  locale={'EN'}
                  isTdsQQ
                  serviceTermUrl={'https://bugly.tds.tencent.com/docs/other/Bugly_Terms_Of_Service'}
                  privacyPolicyUrl={'https://bugly.tds.tencent.com/docs/other/Bugly_Privacy_Policy'}
                  cookiePolicyUrl={'https://bugly.tds.tencent.com/docs/other/Bugly_Cookies_Policy/'}
                />
              </AppSlot>
              <AppSlot apps={[MultiApp.Pro]}>
                <TdsFooterLite
                  isTdsQQ
                />
              </AppSlot>
            </Fragment>
          )}
          <Tools />
        </div>
      </BasicLayout>
    </Spin>
  );
});
